import { cycleStandardNames } from "../InspectionCycles/inspectionCycleValidation";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  getInspectionCycle,
  getEngagementDetail,
  getLoggedInUser,
} from "../../helpers/backend_helper";
import {
  Modal,
  ModalBody,
  ModalHeader,
} from "../../components/Common/AppOverlay";
import InspectionJourneyNav, {
  journeyPath,
} from "../../components/InspectionJourney/InspectionJourneyNav";
import InspectionCycleEngagementList from "./InspectionCycleEngagementList";
import InspectionReviewList from "../InspectionReviews/InspectionReviewList";
import InspectionCycleOverview from "../InspectionCycles/InspectionCycleOverview";
import EngagementReporting from "./EngagementReporting";
import CycleEngagementReviewConfiguration from "./CycleEngagementReviewConfiguration";

const date = (value) =>
  value && !Number.isNaN(new Date(value).getTime())
    ? new Date(value).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const Facts = ({ items }) => (
  <dl className="inspection-journey__facts">
    {items.map(([label, value]) => (
      <div key={label}>
        <dt>{label}</dt>
        <dd>{value || "—"}</dd>
      </div>
    ))}
  </dl>
);

export default function InspectionJourneyPage() {
  const { cycleId, engagementId, stage: requestedStage } = useParams();
  const stage = engagementId ? requestedStage : "engagements";
  const navigate = useNavigate();
  const requestKey = `${cycleId}/${engagementId || ""}`;
  const [settledRequestKey, setSettledRequestKey] = useState(null);
  const [cycle, setCycle] = useState(null);
  const [engagement, setEngagement] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [revision, setRevision] = useState(0);
  const [account, setAccount] = useState(null);
  useEffect(() => {
    let current = true;
    getLoggedInUser()
      .then((value) => {
        if (current) setAccount(value);
      })
      .catch(() => {});
    return () => {
      current = false;
    };
  }, []);
  const roleName =
    account?.role?.name ||
    account?.roleName ||
    account?.userRole?.name ||
    account?.designation ||
    "";
  const canEdit =
    Boolean(account) &&
    /(admin|quality|\bqm\b|inspection\s+team)/i.test(roleName);
  const [cycleEditing, setCycleEditing] = useState(false);

  useEffect(() => {
    let current = true;
    setLoading(true);
    setError("");
    Promise.all([
      getInspectionCycle(cycleId),
      engagementId ? getEngagementDetail(engagementId) : Promise.resolve(null),
    ])
      .then(([cycleResponse, engagementResponse]) => {
        if (
          !cycleResponse?.status ||
          !cycleResponse.data ||
          (engagementId &&
            (!engagementResponse?.status || !engagementResponse.data))
        )
          throw new Error(
            "This cycle or engagement is unavailable for your account."
          );
        const selected = engagementResponse?.data;
        if (
          engagementId &&
          !selected.inspectionCycleIds?.some(
            (id) => Number(id) === Number(cycleId)
          )
        )
          throw new Error(
            "This engagement does not belong to the selected inspection cycle."
          );
        if (current) {
          setCycle(cycleResponse.data);
          setEngagement(selected || null);
        }
      })
      .catch((reason) => {
        if (current)
          setError(reason.message || "Unable to load this workflow.");
      })
      .finally(() => {
        if (current) {
          setSettledRequestKey(requestKey);
          setLoading(false);
        }
      });
    return () => {
      current = false;
    };
  }, [cycleId, engagementId, revision, requestKey]);

  if (loading || settledRequestKey !== requestKey)
    return (
      <div
        className="inspection-journey inspection-journey__empty"
        role="status"
      >
        Loading inspection workflow…
      </div>
    );
  if (
    error ||
    !["engagements", "preparation", "reviews", "reporting"].includes(stage)
  )
    return (
      <div className="inspection-journey inspection-journey__empty">
        <p role="alert">{error || "Unknown workflow step."}</p>
        <Link to="/inspections/list">Back to Inspection Cycles</Link>
        <button
          onClick={() => setRevision((value) => value + 1)}
          className="inspection-journey__button"
        >
          Retry
        </button>
      </div>
    );

  return (
    <>
      {stage !== "engagements" && (
        <InspectionJourneyNav
          cycleId={cycleId}
          engagementId={engagementId}
          stage={stage}
          preparationComplete={Boolean(engagement?.isDataPreparationComplete)}
          guidanceVariant="inline"
        />
      )}
      <main className="inspection-journey piers-page">
        {stage !== "engagements" && (
          <section className="inspection-journey__context">
            <header>
              <div className="piers-page-header">
                <Link to="/inspections/list">Inspection Cycles</Link>
                <h1 className="piers-page-title">
                  {cycle.projectTitle ||
                    cycle.title ||
                    cycle.name ||
                    cycle.projectNo}
                </h1>
              </div>
              <button
                className="inspection-journey__button"
                onClick={() => setCycleEditing(true)}
              >
                View Cycle Details
              </button>
            </header>
            <Facts
              items={[
                ["Cycle ID", cycle.projectNo],
                ["Standards", cycleStandardNames(cycle)],
                ["Review type", cycle.reviewType?.name],
                ["End date / review due date", date(cycle.endDate)],
                ["Status", cycle.projectStatus?.name],
              ]}
            />
          </section>
        )}
        {engagement && (
          <section className="inspection-journey__context">
            <header>
              <h2>{engagement.name || engagement.engagementNo}</h2>
              <Link to={journeyPath(cycleId)}>← Back to Engagements</Link>
            </header>
            <Facts
              items={[
                ["Engagement ID", engagement.engagementNo],
                [
                  "Client",
                  engagement.client?.clientName || engagement.clientName,
                ],
                [
                  "Preparation owner",
                  engagement.assignedUser?.fullName || "Unassigned",
                ],
                ["Status", engagement.engagementStatus?.name],
              ]}
            />
          </section>
        )}
        {stage === "engagements" && (
          <>
            <InspectionCycleEngagementList
              cycle={cycle}
              onViewCycle={() => setCycleEditing(true)}
              key={cycleId}
              dataId={Number(cycleId)}
              onSentForReview={(record) =>
                navigate(journeyPath(cycleId, record.id, "reviews"))
              }
              onOpenEngagement={(record) =>
                navigate(
                  journeyPath(
                    cycleId,
                    record.id,
                    record.isGenerated ? "reviews" : "preparation"
                  )
                )
              }
            />
          </>
        )}
        {stage === "preparation" && (
          <>
            <div className="inspection-journey__note">
              <strong>
                Configure this cycle engagement, then explicitly create its
                inspection review.
              </strong>
              <p>
                Template matching uses the snapshotted review type, industry
                type, and work type. All selections remain editable until the
                review is created.
              </p>
            </div>
            {canEdit ? (
              <CycleEngagementReviewConfiguration
                cycleId={Number(cycleId)}
                engagementId={Number(engagementId)}
                onCreated={() =>
                  navigate(journeyPath(cycleId, engagementId, "reviews"))
                }
              />
            ) : (
              <div className="inspection-journey__note">
                You can view only the cycle engagements assigned to you.
              </div>
            )}
            <footer className="inspection-journey__actions">
              <Link to={journeyPath(cycleId)}>Back to Engagements</Link>
              <Link
                className="inspection-journey__button"
                to={journeyPath(cycleId, engagementId, "reviews")}
              >
                View Reviews →
              </Link>
            </footer>
          </>
        )}
        {stage === "reviews" && (
          <>
            <div className="inspection-journey__note">
              Start or continue your assigned reviews below. Open a review to
              see its checklists, queries, findings, and sign-offs.
            </div>
            <InspectionReviewList
              key={engagementId}
              engagementId={Number(engagementId)}
            />
            <footer className="inspection-journey__actions">
              <Link to={journeyPath(cycleId, engagementId)}>← Preparation</Link>
              <Link
                className="inspection-journey__button"
                to={journeyPath(cycleId, engagementId, "reporting")}
              >
                View Reports &amp; Sign-offs →
              </Link>
            </footer>
          </>
        )}
        {stage === "reporting" && (
          <EngagementReporting cycleId={cycleId} engagementId={engagementId} />
        )}
      </main>
      <Modal isOpen={cycleEditing} size="lg" backdrop="static">
        <ModalHeader toggle={() => setCycleEditing(false)}>
          Cycle Details
        </ModalHeader>
        <ModalBody>
          {cycleEditing && (
            <InspectionCycleOverview
              inspectionCycleId={Number(cycleId)}
              canCreateInspection={canEdit}
            />
          )}
        </ModalBody>
      </Modal>
    </>
  );
}
